import { MecSize, TechSpec } from '@adapters/bigcommerce/types/product';
import { BigCommerceLocaleString } from '@adapters/bigcommerce/types/locale';
import {
  NameEntityOrDescriptionEntity,
  RowEntity,
  SizeChartInterface,
} from '@adapters/bigcommerce/types/sizeChart';

interface MeasurementSpec {
  /** Stable, locale-independent id for the derived chart. */
  id: string;
  /** The tech spec keys holding the measurement, per locale, most specific first. */
  keys: Record<BigCommerceLocaleString, readonly string[]>;
  /** Row label, e.g. "Head circumference". */
  name: NameEntityOrDescriptionEntity;
  /** How-to-measure copy for the "Find your measurements" tab. */
  description: NameEntityOrDescriptionEntity;
  /**
   * How the feed writes the measurement.
   *
   * - `range` — a numeric range per size (`51-55cm (Small)`), rendered in cm with
   *   a converted inches row.
   * - `verbatim` — not a range to convert. A ski's `136-125-110mm (175cm)` is a
   *   tip-waist-tail width, so it is shown exactly as written, under the size it
   *   belongs to.
   */
  format: 'range' | 'verbatim';
}

/**
 * Tech specs that hold a per-size measurement range.
 *
 * PIT does not author a size chart for every helmet/PFD/ski style, but the ERP
 * feed already carries the measurement per size in the tech specs table.
 * Supporting a new category means adding one entry here and pointing the
 * category's `size_chart` value at it in `SIZE_CHART_SPEC_IDS`.
 *
 * Copy matches the authored charts (e.g. `wrsi_unisex_helmets_alpha`) so a
 * derived chart is indistinguishable from an authored one.
 */
export const MEASUREMENT_SPECS: readonly MeasurementSpec[] = [
  {
    id: 'circumference',
    keys: { en: ['Circumference'], fr: ['Circonférence'] },
    name: { en: 'Head circumference', fr: 'Tour de tête' },
    description: {
      en: 'Measure circumference above brow ridges and above ears.',
      fr: 'Mesurer la circonférence de la tête au-dessus des arcades sourcilières et des oreilles.',
    },
    format: 'range',
  },
  {
    id: 'chest_circumference',
    keys: {
      en: ['Chest circumference', 'Chest'],
      // The FR feed writes some PFDs' chest as a bare `Circonférence`. Safe only
      // because `size_chart` already says this product is a PFD.
      fr: [
        'Tour de poitrine',
        'Circonférence de poitrine',
        'Poitrine',
        'Circonférence',
      ],
    },
    name: { en: 'Chest circumference', fr: 'Tour de poitrine' },
    description: {
      en: 'Measure around the fullest part of your chest, keeping the tape level under your arms.',
      fr: 'Mesurer le tour de poitrine à l’endroit le plus fort, en gardant le ruban à l’horizontale sous les bras.',
    },
    format: 'range',
  },
  {
    id: 'dimension',
    keys: { en: ['Dimension', 'Dimensions'], fr: ['Dimension', 'Dimensions'] },
    name: { en: 'Dimension', fr: 'Dimensions' },
    description: {
      en: 'Tip, waist and tail widths, listed for each ski length.',
      fr: 'Largeurs de la spatule, du patin et du talon, pour chaque longueur de ski.',
    },
    format: 'verbatim',
  },
  {
    // Cross-country skis share one width across every length and are sized by
    // the skier's weight instead (Rossignol X-Tour Escape: `41-48kg (176cm)`).
    id: 'user_weight',
    keys: {
      en: ['User weight range', 'User weight'],
      fr: ['Poids de l’utilisateur', "Poids de l'utilisateur"],
    },
    name: { en: 'User weight range', fr: 'Poids de l’utilisateur' },
    description: {
      en: 'Choose the ski length that matches your body weight.',
      fr: 'Choisir la longueur de ski qui correspond à votre poids.',
    },
    // kg has no place on the cm/in toggle, so show it as the feed writes it.
    format: 'verbatim',
  },
];

/**
 * `size_chart` metafield value → the measurement specs its chart may be built
 * from, in order of preference. The first that yields a chart wins.
 *
 * This is the lever PIT controls: naming a chart `skis` builds it from the
 * Dimension tech spec (or, failing that, User weight range), `helmets` from
 * Circumference, and `pfd` from Chest circumference, so merch decides which
 * measurement a derived chart shows instead of the code guessing from whatever
 * spec happens to be present.
 *
 * A value that names no category derives nothing. Guessing from whichever spec
 * happens to be present mislabels the many non-helmet products that also carry a
 * `Circumference` (gloves, hats, hip belts) as a head measurement.
 */
export const SIZE_CHART_SPEC_IDS: Readonly<Record<string, readonly string[]>> =
  {
    ski: ['dimension', 'user_weight'],
    skis: ['dimension', 'user_weight'],
    helmet: ['circumference'],
    helmets: ['circumference'],
    pfd: ['chest_circumference'],
    pfds: ['chest_circumference'],
  };

const normaliseKey = (key: string) => key.trim().toLowerCase();

/**
 * The specs the `size_chart` value asks for, in preference order.
 *
 * Charts are named per category, either bare (`skis`) or namespaced
 * (`wrsi_unisex_helmets_alpha`), so the category is matched as a whole word.
 * Substring matching would read `skirts` as a ski and `pfds` out of anything.
 */
const findSpecs = (sizeChartId: string | null | undefined) => {
  const words = normaliseKey(sizeChartId ?? '').split(/[^a-z0-9]+/);
  const wantedIds =
    words.map((word) => SIZE_CHART_SPEC_IDS[word]).find(Boolean) ?? [];

  return wantedIds
    .map((id) => MEASUREMENT_SPECS.find((spec) => spec.id === id))
    .filter((spec): spec is MeasurementSpec => spec !== undefined);
};

const findTechSpec = (
  techSpecs: TechSpec[],
  spec: MeasurementSpec,
  locale: BigCommerceLocaleString
) => {
  // Deliberately locale-only: an English key on a French PDP carries English
  // size labels with it, and no chart beats a half-translated one.
  return spec.keys[locale]
    .map((key) =>
      techSpecs.find(
        (item) => item.key && normaliseKey(item.key) === normaliseKey(key)
      )
    )
    .find(Boolean);
};

/**
 * `51-55cm (Small)`, `51-55 cm (Petit)`, `58cm (One size)`, en-dash variants.
 * The label is optional: a one-size product's spec is often a bare `76-132cm`.
 */
const MEASUREMENT_ENTRY =
  /^\s*([\d.]+)\s*(?:[-–]\s*([\d.]+))?\s*(cm|mm|in|po)?\s*(?:\(([^)]+)\))?\s*$/i;

const CM_PER_INCH = 2.54;

interface ParsedEntry {
  min: number;
  max: number;
  /** Normalised to `cm` or `in`. */
  unit: 'cm' | 'in';
  sizeLabel: string;
}

/** Authored charts express inches in quarters (19, 21.25, 21.75), so match that. */
const toInches = (cm: number) => Math.round((cm / CM_PER_INCH) * 4) / 4;

/** Trims trailing zeros, so 21.25 stays `21.25` while 19.0 renders as `19`. */
const formatNumber = (value: number) => String(Number(value.toFixed(2)));

const splitEntries = (value: string | string[]): string[] =>
  (Array.isArray(value) ? value : value.split(/<br\s*\/?>/i))
    .map((entry) => entry.trim())
    .filter(Boolean);

const parseEntry = (
  entry: string,
  fallbackLabel: string | undefined
): ParsedEntry | null => {
  const match = MEASUREMENT_ENTRY.exec(entry);
  if (!match) return null;

  const [, rawMin, rawMax, rawUnit, rawLabel] = match;
  let min = Number.parseFloat(rawMin);
  let max = rawMax ? Number.parseFloat(rawMax) : min;
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;

  const sizeLabel = rawLabel?.trim() || fallbackLabel;
  if (!sizeLabel) return null;

  const sourceUnit = (rawUnit ?? 'cm').toLowerCase();
  if (sourceUnit === 'mm') {
    min /= 10;
    max /= 10;
  }
  const unit = sourceUnit === 'in' || sourceUnit === 'po' ? 'in' : 'cm';

  return { min, max, unit, sizeLabel };
};

const buildMeasurementRow = ({
  unit,
  entries,
  convert,
  spec,
}: {
  unit: string;
  entries: ParsedEntry[];
  convert: (value: number) => number;
  spec: MeasurementSpec;
}): RowEntity => ({
  // Both unit rows share one title so `useMeasureData` dedupes them into a single
  // "Find your measurements" instruction, exactly as authored charts do.
  title: `key.derived_${spec.id}`,
  unit,
  name: [spec.name],
  description: [spec.description],
  // Data rows are flat min,max pairs; SizeChartView joins each pair with a dash.
  // A one-size entry (`58cm (One size)`) has no range, so its partner stays blank
  // rather than rendering as `58-58`.
  values: {
    value: entries.flatMap(({ min, max }) => {
      const low = formatNumber(convert(min));
      const high = formatNumber(convert(max));
      return [low, high === low ? '' : high];
    }),
  },
});

/**
 * `oneSizeLabel` names an unlabelled range, but only when it is the spec's sole
 * entry: with several ranges there is no telling which size each belongs to.
 */
const parseRangeEntries = (
  value: string | string[],
  oneSizeLabel: string | undefined
): ParsedEntry[] => {
  const entries = splitEntries(value);
  const fallbackLabel = entries.length === 1 ? oneSizeLabel : undefined;

  return (
    entries
      .map((entry) => parseEntry(entry, fallbackLabel))
      .filter((entry): entry is ParsedEntry => entry !== null)
      // The feed is not consistently ordered (e.g. Smith Vantage runs X-Large → Small).
      .sort((a, b) => a.min - b.min || a.max - b.max)
  );
};

/** `136-125-110mm (175cm)`: any measurement text, labelled by the size it fits. */
const VERBATIM_ENTRY = /^\s*(.+?)\s*\(([^)]+)\)\s*$/;

interface VerbatimEntry {
  /** The measurement exactly as the feed writes it. */
  text: string;
  sizeLabel: string;
}

const parseVerbatimEntry = (entry: string): VerbatimEntry | null => {
  const match = VERBATIM_ENTRY.exec(entry);
  if (!match) return null;

  const text = match[1].trim();
  const sizeLabel = match[2].trim();
  if (!text || !sizeLabel) return null;

  return { text, sizeLabel };
};

/** Leading number of a size label (`175cm` → 175), or `null` when it has none. */
const labelOrder = (sizeLabel: string): number | null => {
  const match = /[\d.]+/.exec(sizeLabel);
  const value = match ? Number.parseFloat(match[0]) : Number.NaN;
  return Number.isFinite(value) ? value : null;
};

const parseVerbatimEntries = (value: string | string[]): VerbatimEntry[] => {
  const entries = splitEntries(value)
    .map(parseVerbatimEntry)
    .filter((entry): entry is VerbatimEntry => entry !== null);

  const orders = entries.map((entry) => labelOrder(entry.sizeLabel));
  // Sort by size when every label is numeric (skis run 175/181/189); otherwise the
  // feed's own order is the only ordering available.
  if (orders.some((order) => order === null)) return entries;

  return entries
    .map((entry, index) => ({ entry, order: orders[index] as number }))
    .sort((a, b) => a.order - b.order)
    .map(({ entry }) => entry);
};

// Column headers are duplicated; SizeChartView keeps every other index.
const buildHeaderRow = (sizeLabels: string[]): RowEntity => ({
  title: 'key.measurement',
  unit: 'key.unit_type',
  name: [{ en: '', fr: '' }],
  description: [{ en: '', fr: '' }],
  values: { value: sizeLabels.flatMap((label) => [label, label]) },
});

const buildRangeRows = (
  entries: ParsedEntry[],
  spec: MeasurementSpec
): RowEntity[] | null => {
  if (entries.length === 0) return null;

  const isImperial = entries[0].unit === 'in';

  const rows: RowEntity[] = [
    buildHeaderRow(entries.map(({ sizeLabel }) => sizeLabel)),
    buildMeasurementRow({
      unit: isImperial ? 'key.unit_in' : 'key.unit_cm',
      entries,
      convert: (value) => value,
      spec,
    }),
  ];

  // Give metric charts an imperial row too, so the cm/in toggle behaves the
  // same as it does on authored charts.
  if (!isImperial) {
    rows.push(
      buildMeasurementRow({
        unit: 'key.unit_in',
        entries,
        convert: toInches,
        spec,
      })
    );
  }

  return rows;
};

const buildVerbatimRows = (
  entries: VerbatimEntry[],
  spec: MeasurementSpec
): RowEntity[] | null => {
  if (entries.length === 0) return null;

  return [
    buildHeaderRow(entries.map(({ sizeLabel }) => sizeLabel)),
    {
      title: `key.derived_${spec.id}`,
      // No unit: the measurement carries its own and there is nothing to convert,
      // so the row renders whichever way the cm/in toggle is set.
      unit: '',
      name: [spec.name],
      description: [spec.description],
      // SizeChartView reads values pairwise; the blank partner keeps one size's
      // measurement from being joined onto the next.
      values: { value: entries.flatMap(({ text }) => [text, '']) },
    },
  ];
};

/**
 * Builds a size chart from a product's tech specs, in the same shape the authored
 * S3 payloads use, so every existing renderer displays it without modification.
 *
 * `sizes` are the product's size options. A product with exactly one lends its
 * name to a range the feed wrote without a label (`76-132cm` → `One Size`).
 *
 * Returns `null` when the product has no measurement spec we can parse.
 */
export function deriveSizeChartFromTechSpecs(
  techSpecs: TechSpec[] | undefined | null,
  locale: BigCommerceLocaleString,
  sizeChartId?: string | null,
  sizes?: readonly MecSize[] | null
): SizeChartInterface | null {
  if (!techSpecs?.length) return null;

  const oneSizeLabel =
    sizes?.length === 1 ? sizes[0].name?.trim() || undefined : undefined;

  for (const spec of findSpecs(sizeChartId)) {
    const value = findTechSpec(techSpecs, spec, locale)?.value;
    const rows =
      value &&
      (spec.format === 'verbatim'
        ? buildVerbatimRows(parseVerbatimEntries(value), spec)
        : buildRangeRows(parseRangeEntries(value, oneSizeLabel), spec));

    if (rows) {
      return {
        id: `derived:${spec.id}`,
        imageFileName: [],
        tables: { table: { row: rows } },
      };
    }
  }

  return null;
}

export default deriveSizeChartFromTechSpecs;
